import React, { useState, useEffect, useCallback } from 'react';
import { 
    Activity, Loader2, PackageCheck, CheckCircle2, Check, X, Clock, 
    CheckSquare, Send, AlertTriangle, Package, XCircle
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const LancementProduction = () => {
    // --- ÉTATS DES LISTES ---
    const [demandesValidees, setDemandesValidees] = useState([]); 
    const [productionsEnCours, setProductionsEnCours] = useState([]); 
    const [productionsTerminees, setProductionsTerminees] = useState([]); 
    
    // --- ÉTATS D'INTERFACE ---
    const [activeTab, setActiveTab] = useState('a_lancer'); 
    const [selectedId, setSelectedId] = useState(null);
    const [detailsCurrentOrder, setDetailsCurrentOrder] = useState([]);
    const [quantities, setQuantities] = useState({}); 
    const [validatedLines, setValidatedLines] = useState({}); 
    const [observations, setObservations] = useState({}); 
    const [qteProduite, setQteProduite] = useState({}); 

    // --- ÉTATS DE CHARGEMENT ---
    const [loading, setLoading] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // --- ÉTAT DU SYSTÈME DE NOTIFICATION TOAST ---
    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success', 
        message: '',
        onConfirm: null
    });

    const primaryColor = '#b45309'; 
    const borderColor = '#e2e8f0';

    // --- RÉCUPÉRATION DES DONNÉES ---
    const fetchAllData = useCallback(async () => {
        setLoading(true);
        try {
            const [resPrets, resEnCours, resTerminees] = await Promise.all([
                API.get('/production/lancement/prets'),
                API.get('/production/en-cours'),
                API.get('/production/terminees')
            ]);
            setDemandesValidees(resPrets.data || []);
            setProductionsEnCours(resEnCours.data || []);
            setProductionsTerminees(resTerminees.data || []);
            
            const initialQtes = {};
            (resEnCours.data || []).forEach(p => { initialQtes[p.id_besoin] = p.quantite; });
            setQteProduite(initialQtes);
        } catch (err) {
            console.error("Erreur fetch:", err);
            if (typeof showToast === "function") showToast("Erreur lors de la récupération des données", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    // --- GESTION DU TOAST (AUTO-FERMETURE) ---
    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null });
    };

    const showConfirm = (message, onConfirm) => {
        setNotification({ isOpen: true, type: 'confirm', message, onConfirm });
    };

    // --- LOGIQUE SOCKET.IO ---
    useEffect(() => {
        fetchAllData();
        if (!socket) return;
        const user = JSON.parse(localStorage.getItem('user'));
        const companyId = user?.company_id || user?.companyId;
        if (companyId) {
            socket.emit('join_company', companyId.toString());
        }

        const handleRefresh = (data) => {
            if (!data) return; 
            const isRelevantModule = ['PRODUCTION', 'STOCKS', 'PSF'].includes(data.module);
            const isKnownAction = [
                'NEW_LANCEMENT', 'FINISH_PRODUCTION', 'REFRESH_ALL', 
                'LANCEMENT', 'TERMINE', 'PRODUCTION_NEED_UPDATED', 
                'RAW_MATERIAL_CHANGED', 'STOCKE', 'UPDATE'
            ].includes(data.action || data.type);
            
            if (isRelevantModule || isKnownAction) {
                setTimeout(() => fetchAllData(), 600);
            }
        };

        socket.on('REFRESH_UI', handleRefresh);
        socket.on('PRODUCTION_UPDATED', handleRefresh);
        socket.on('PRODUCTION_NEED_UPDATED', handleRefresh);
        socket.on('FORCE_REFRESH', fetchAllData);

        return () => {
            socket.off('REFRESH_UI', handleRefresh);
            socket.off('PRODUCTION_UPDATED', handleRefresh);
            socket.off('PRODUCTION_NEED_UPDATED', handleRefresh);
            socket.off('FORCE_REFRESH', fetchAllData);
        };
    }, [fetchAllData]);

    // --- ACTIONS LOGIQUES ---
    const handleSelectOrder = async (id) => {
        if (activeTab !== 'a_lancer') return;
        setSelectedId(id);
        setLoadingDetails(true);
        setValidatedLines({}); 
        try {
            const res = await API.get(`/production/lancement/details/${id}`);
            const data = res.data || [];
            setDetailsCurrentOrder(data);
            
            const initialQtys = {};
            data.forEach(item => { 
                const key = item.id_reservation?.toString() || (item.matiere_id || item.composant_semi_fini_id)?.toString(); 
                if (key) initialQtys[key] = item.quantite_theorique; 
            });
            setQuantities(initialQtys);
        } catch (err) {
            showToast("Erreur lors du chargement des matières premières.", "error");
        } finally {
            setLoadingDetails(false);
        }
    };

    const validateLine = (id) => {
        const mId = id?.toString();
        if (!mId || !quantities[mId] || parseFloat(quantities[mId]) <= 0) {
            return showToast("Veuillez saisir une quantité supérieure à 0.", "error");
        }
        setValidatedLines(prev => ({ ...prev, [mId]: true }));
    };

    const resetLine = (id) => {
        setValidatedLines(prev => ({ ...prev, [id.toString()]: false })); 
    };

    const confirmDemarrerProduction = (id) => {
        if (detailsCurrentOrder.length === 0) return;
        
        const allLinesValidated = detailsCurrentOrder.every(item => {
            const key = item.id_reservation?.toString() || (item.matiere_id || item.composant_semi_fini_id)?.toString();
            return validatedLines[key];
        });
        
        if (!allLinesValidated) {
            return showToast("Veuillez confirmer chaque ligne d'ingrédient.", "error");
        }
        showConfirm(
            "Lancer la production ? Les stocks seront déduits immédiatement.",
            () => demarrerProduction(id)
        );
    };

    const demarrerProduction = async (id) => {
        setIsProcessing(true);
        try {
            const payload = {
                articles_confirmes: detailsCurrentOrder.map(item => {
                    const key = item.id_reservation?.toString() || (item.matiere_id || item.composant_semi_fini_id).toString();
                    const qteSaisie = parseFloat(quantities[key]);
                    
                    return {
                        id_reservation: item.id_reservation, 
                        matiere_id: item.matiere_id,
                        composant_semi_fini_id: item.composant_semi_fini_id,
                        quantite_reelle: isNaN(qteSaisie) ? item.quantite_theorique : qteSaisie
                    };
                })
            };
            
            await API.put(`/production/lancement/valider/${id}`, payload);
            setNotification({ isOpen: false });
            setSelectedId(null);
            setDetailsCurrentOrder([]);
            setValidatedLines({});
            
            showToast("Production lancée avec succès.");
            fetchAllData(); 
        } catch (err) {
            showToast(err.response?.data?.error || "Une erreur est survenue.", "error");
        } finally {
            setIsProcessing(false);
        }
    };

    const confirmTerminerProduction = (id) => {
        const qte = parseFloat(qteProduite[id]);
        if (isNaN(qte) || qte <= 0) {
            return showToast("La quantité réelle produite est obligatoire.", "error");
        }
        showConfirm(
            `Clôturer la production et stocker ${qte} unité(s) ?`,
            () => terminerProduction(id)
        );
    };

    const terminerProduction = async (id) => {
        setIsProcessing(true);
        try {
            await API.put(`/production/terminer/${id}`, { 
                observation_finale: observations[id] || "",
                quantite_produite: parseFloat(qteProduite[id])
            });
            setNotification({ isOpen: false });
            showToast("Production clôturée et stockée.");
            fetchAllData();
        } catch (err) {
            showToast(err.response?.data?.error || "Erreur lors de la clôture.", "error");
        } finally {
            setIsProcessing(false);
        }
    };

    // --- RENDU DU TOAST ---
    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const isConfirm = notification.type === 'confirm';
        const color = notification.type === 'error' ? '#ef4444' : (isConfirm ? primaryColor : '#10b981');
        return (
            <div style={{
                position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
                zIndex: 3000, display: 'flex', alignItems: 'center',
                background: 'white', padding: '12px 20px', borderRadius: '15px',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', border: `1px solid ${borderColor}`,
                minWidth: '400px', transition: 'all 0.3s ease', borderLeft: `8px solid ${color}`
            }}>
                <div style={{ 
                    display: 'flex', justifyContent: 'center', alignItems: 'center', 
                    width: '35px', height: '35px', borderRadius: '50%', 
                    background: `${color}15`, marginRight: '15px', flexShrink: 0
                }}>
                    {notification.type === 'error' ? <AlertTriangle size={20} color={color} /> : 
                     isConfirm ? <PackageCheck size={20} color={color} /> : 
                     <CheckCircle2 size={20} color={color} />}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>{notification.message}</p>
                    {isConfirm && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button disabled={isProcessing} onClick={notification.onConfirm} style={{ background: '#0f172a', color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}>
                                {isProcessing ? 'Lancement...' : 'CONFIRMER'}
                            </button>
                            <button onClick={() => setNotification({isOpen: false})} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}>ANNULER</button>
                        </div>
                    )}
                </div>
                {!isConfirm && (
                    <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}>
                        <X size={20} />
                    </button>
                )}
            </div>
        );
    };

    const tabStyle = (id) => ({
        padding: '12px 24px',
        cursor: 'pointer',
        fontWeight: '700',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        borderBottom: activeTab === id ? `3px solid ${primaryColor}` : '3px solid transparent',
        color: activeTab === id ? primaryColor : '#64748b',
        transition: 'all 0.2s'
    });

    return (
        <div style={{ display: 'flex', background: '#f1f5f9', height: '100vh', width: '100vw', overflow: 'hidden', position: 'relative' }}>
            <Sidebar />
            <NotificationToast />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <header style={{ padding: '20px 30px', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
                    <h2 style={{ color: '#0f172a', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '12px', margin: '0 0 15px 0', fontSize: '20px' }}>
                        <Activity size={28} color={primaryColor} strokeWidth={2.5} /> WORKFLOW PRODUCTION
                    </h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div onClick={() => setActiveTab('a_lancer')} style={tabStyle('a_lancer')}>
                            <PackageCheck size={18} /> À LANCER <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '12px', fontSize: '11px' }}>{demandesValidees.length}</span>
                        </div>
                        <div onClick={() => setActiveTab('en_cours')} style={tabStyle('en_cours')}>
                            <Clock size={18} /> EN COURS <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontSize: '11px' }}>{productionsEnCours.length}</span>
                        </div>
                        <div onClick={() => setActiveTab('terminee')} style={tabStyle('terminee')}>
                            <CheckSquare size={18} /> TERMINÉES <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '12px', fontSize: '11px' }}>{productionsTerminees.length}</span>
                        </div>
                    </div>
                </header>

                <main style={{ flex: 1, padding: '25px', display: 'flex', gap: '25px', overflow: 'hidden' }}>
                    <div style={{ width: activeTab === 'a_lancer' ? '35%' : '100%', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                            {loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Loader2 className="animate-spin" color={primaryColor} size={32} /></div>
                            ) : (
                                <>
                                    {activeTab === 'a_lancer' && demandesValidees.map(dem => (
                                        <div key={dem.id_besoin} onClick={() => handleSelectOrder(dem.id_besoin)} style={{ padding: '18px', borderRadius: '12px', border: '2px solid', marginBottom: '12px', cursor: 'pointer', transition: 'all 0.2s', background: selectedId === dem.id_besoin ? '#fff7ed' : '#fff', borderColor: selectedId === dem.id_besoin ? primaryColor : '#f1f5f9' }}>
                                            <div style={{ fontWeight: '900', fontSize: '14px', color: selectedId === dem.id_besoin ? primaryColor : '#1e293b' }}>ORDRE #{dem.id_besoin.toString().slice(-6)}</div>
                                            <div style={{ fontWeight: '600', fontSize: '13px', color: '#475569', marginTop: '6px' }}>📦 {dem.article_nom}</div>
                                            <div style={{ fontWeight: '800', fontSize: '12px', color: '#0284c7', marginTop: '4px' }}>Volume : {dem.quantite} {dem.unite}</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={12}/> {new Date(dem.created_at).toLocaleDateString()}</div>
                                        </div>
                                    ))}

                                    {activeTab === 'en_cours' && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                                            {productionsEnCours.map(prod => (
                                                <div key={prod.id_besoin} style={{ padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', background: '#fff', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                        <span style={{ fontSize: '10px', fontWeight: '800', color: primaryColor, background: '#fff7ed', padding: '4px 10px', borderRadius: '20px' }}>EN COURS</span>
                                                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>#{prod.id_besoin.toString().slice(-6)}</span>
                                                    </div>
                                                    <h4 style={{ margin: '0 0 8px 0', color: '#0f172a', fontWeight: '800' }}>{prod.article_nom}</h4>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f1f5f9', padding: '12px', borderRadius: '10px', marginBottom: '15px' }}>
                                                        <div>
                                                            <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Prévu: <b>{prod.quantite} {prod.unite}</b></p>
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <p style={{ fontSize: '11px', color: primaryColor, margin: 0, fontWeight: '700' }}>RÉEL PRODUIT *</p>
                                                            <input 
                                                                type="number"
                                                                step="any"
                                                                value={qteProduite[prod.id_besoin] || ''}
                                                                onChange={(e) => setQteProduite({...qteProduite, [prod.id_besoin]: e.target.value})}
                                                                style={{ width: '80px', border: `2px solid ${primaryColor}`, borderRadius: '6px', textAlign: 'center', fontWeight: '900', fontSize: '16px', padding: '4px' }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <button onClick={() => confirmTerminerProduction(prod.id_besoin)} style={{ width: '100%', background: '#16a34a', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                        <CheckCircle2 size={18} /> TERMINER & STOCKER
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {activeTab === 'terminee' && (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                   <tr style={{ textAlign: 'left', fontSize: '12px', color: '#64748b', borderBottom: '2px solid #f1f5f9' }}>
                                                        <th style={{ padding: '15px' }}>REFERENCE</th>
                                                        <th style={{ padding: '15px' }}>PRODUIT</th>
                                                        <th style={{ padding: '15px' }}>QTE PRÉVUE</th>
                                                        <th style={{ padding: '15px' }}>QTE RÉELLE</th>
                                                        <th style={{ padding: '15px' }}>STATUT</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {productionsTerminees.map(p => (
                                                        <tr key={p.id_besoin} style={{ borderBottom: '1px solid #f8fafc', fontSize: '13px' }}>
                                                            <td style={{ padding: '15px', color: '#94a3b8' }}>#{p.id_besoin.toString().slice(-8)}</td>
                                                            <td style={{ padding: '15px', fontWeight: '700' }}>{p.article_nom}</td>
                                                            <td style={{ padding: '15px' }}>{p.quantite} {p.unite}</td>
                                                            <td style={{ padding: '15px' }}>
                                                                <span style={{ fontWeight: '900', color: '#16a34a', background: '#f0fdf4', padding: '4px 8px', borderRadius: '6px' }}>
                                                                    {p.quantite_produite || '--'} 
                                                                </span>
                                                                {p.quantite_produite ? ` ${p.unite}` : ''}
                                                            </td>
                                                            <td style={{ padding: '15px' }}><span style={{ color: '#16a34a', fontWeight: '800', fontSize: '11px' }}>TERMINÉ</span></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {activeTab === 'a_lancer' && (
                        <div style={{ width: '65%', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                            {!selectedId ? (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#cbd5e1' }}>
                                    <Package size={100} strokeWidth={1} />
                                    <p style={{ fontWeight: '700', marginTop: '15px', fontSize: '18px' }}>Sélectionnez un ordre</p>
                                </div>
                            ) : loadingDetails ? (
                                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Loader2 className="animate-spin" size={48} color={primaryColor} /></div>
                            ) : (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '30px', overflow: 'hidden' }}>
                                    <div style={{ marginBottom: '25px', borderLeft: `5px solid ${primaryColor}`, paddingLeft: '20px' }}>
                                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '900' }}>Contrôle des Ingrédients</h3>
                                    </div>
                                    <div style={{ flex: 1, overflowY: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px' }}>
                                            <tbody>
                                                {detailsCurrentOrder.map((item) => {
                                                    const key = item.id_reservation?.toString() || (item.matiere_id || item.composant_semi_fini_id)?.toString();
                                                    const isValidated = validatedLines[key];
                                                    return (
                                                        <tr key={key} style={{ background: isValidated ? '#f0fdf4' : '#f8fafc', borderRadius: '12px' }}>
                                                            <td style={{ padding: '18px 15px', borderRadius: '12px 0 0 12px' }}>
                                                                <div style={{ fontWeight: '800', color: '#1e293b', fontSize: '13px' }}>{item.article_nom || "Article inconnu"}</div>
                                                            </td>
                                                            <td style={{ padding: '10px', fontWeight: '700', fontSize: '13px' }}>{item.quantite_theorique} {item.unite}</td>
                                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                                <input 
                                                                    type="number" 
                                                                    step="any"
                                                                    value={quantities[key] ?? ''} 
                                                                    onChange={(e) => setQuantities({...quantities, [key]: e.target.value})} 
                                                                    readOnly={isValidated} 
                                                                    style={{ width: '90px', padding: '8px', textAlign: 'center', fontWeight: '900', borderRadius: '8px', border: '1px solid #e2e8f0' }} 
                                                                />
                                                            </td>
                                                            <td style={{ padding: '10px 15px', textAlign: 'right', borderRadius: '0 12px 12px 0' }}>
                                                                <button onClick={() => isValidated ? resetLine(key) : validateLine(key)} style={{ background: isValidated ? '#ef4444' : '#22c55e', color: 'white', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}>
                                                                    {isValidated ? <X size={18} /> : <Check size={18} />}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        onClick={() => confirmDemarrerProduction(selectedId)} 
                                        style={{ marginTop: '20px', width: '100%', background: '#0f172a', color: 'white', padding: '18px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                                    >
                                        <CheckCircle2 size={20}/> LANCER LA PRODUCTION
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default LancementProduction;